import React, { useCallback, useRef, useState } from 'react';
import qs from 'qs';
import { BadgeAlt as Badge, Combobox } from '@cargomatic/ui';
import client from 'utils/rest/AdminClient';
const MIN_QUERY_LENGTH = 2;
const SELECT_FIELDS = 'optimized_name name _type default_driver picture email authorized scac_code status is_broker';
function carrierStatusBadgeClass(status) {
    const key = (status || '').toLowerCase();
    switch (key) {
        case 'trained':
        case 'active':
            return 'bg-green-50 text-green-700 border-green-200';
        case 'pending':
        case 'awaiting':
        case 'in_review':
            return 'bg-yellow-50 text-yellow-800 border-yellow-200';
        case 'suspended':
        case 'inactive':
        case 'declined':
            return 'bg-red-50 text-red-700 border-red-200';
        default:
            return 'bg-gray-50 text-gray-700 border-gray-200';
    }
}
async function fetchCarriers(name) {
    var _a;
    const query = {
        page: 1,
        per_page: 25,
        paginate: true,
        sort_by: 'optimized_name',
        order: 'asc',
        test: false,
        lean: true,
        select: SELECT_FIELDS,
        _type: 'Carrier',
        name,
    };
    const res = (await client({
        method: 'GET',
        path: `admin/api/v2/users?${qs.stringify(query)}`,
    }));
    return ((_a = res === null || res === void 0 ? void 0 : res.entity) === null || _a === void 0 ? void 0 : _a.items) || [];
}
function renderCarrierItem(item) {
    return (React.createElement("div", { className: "flex w-full items-center gap-2 min-w-0" },
        React.createElement("span", { className: "font-medium truncate" }, item.name),
        item.scac_code && (React.createElement("span", { className: "text-xs text-muted-foreground" },
            "\u00B7 ",
            item.scac_code)),
        item.email && (React.createElement("span", { className: "text-xs text-muted-foreground truncate" },
            "\u00B7 ",
            item.email)),
        React.createElement("span", { className: "ml-auto flex items-center gap-1 shrink-0" },
            item.is_broker && (React.createElement(Badge, { variant: "outline", className: "bg-purple-50 text-purple-700 border-purple-200" }, "Broker")),
            item.status && (React.createElement(Badge, { variant: "outline", className: carrierStatusBadgeClass(item.status) }, item.status)))));
}
export function CarrierSearchCombobox(props) {
    const { placeholder = 'Search for Carrier', disabled, id, className, multiple = false, value, onValueChange, } = props;
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const requestSeq = useRef(0);
    const handleSearch = useCallback(async (query) => {
        const seq = ++requestSeq.current;
        if (!query || query.length < MIN_QUERY_LENGTH) {
            setItems([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const results = await fetchCarriers(query);
            if (seq === requestSeq.current)
                setItems(results);
        }
        catch (e) {
            console.error(e);
            if (seq === requestSeq.current)
                setItems([]);
        }
        finally {
            if (seq === requestSeq.current)
                setIsLoading(false);
        }
    }, []);
    const comboboxProps = {
        multiple,
        items,
        isLoading,
        onSearch: handleSearch,
        debounceMs: 300,
        itemToLabel: (item) => item.name || '',
        itemToValue: (item) => item._id,
        placeholder,
        disabled,
        id,
        className,
        onValueChange,
        renderItem: renderCarrierItem,
    };
    if (value !== undefined)
        comboboxProps.value = value;
    return (React.createElement(Combobox, Object.assign({}, comboboxProps)));
}
